import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Gavel,
  CheckCircle,
  Timeline,
  Assignment,
  FactCheck,
  Balance,
  AccountBalance,
  EmojiEvents,
} from '@mui/icons-material';

const appealTypes = [
  {
    title: 'First-tier Tribunal Appeal',
    description: 'Challenge visa refusals on human rights or protection grounds',
    timeframe: '14 days (in-country) or 28 days (out of country)',
    grounds: [
      'Human rights claims',
      'Protection claims',
      'EU Settlement Scheme',
      'EEA regulations',
    ],
  },
  {
    title: 'Administrative Review',
    description: 'Request review of eligible visa decisions',
    timeframe: '14 days from decision',
    grounds: [
      'Caseworker error',
      'Document assessment error',
      'Immigration rules misapplication',
      'New evidence consideration',
    ],
  },
  {
    title: 'Upper Tribunal Appeal',
    description: 'Appeal First-tier Tribunal decisions on points of law',
    timeframe: '14 days from First-tier decision',
    grounds: [
      'Error of law',
      'Procedural irregularity',
      'Jurisdictional error',
      'Human rights considerations',
    ],
  },
  {
    title: 'Judicial Review',
    description: 'Challenge immigration decisions with no right of appeal',
    timeframe: '3 months from decision',
    grounds: [
      'Illegality',
      'Irrationality',
      'Procedural unfairness',
      'Human rights violations',
    ],
  },
];

const services = [
  'Case assessment and strategy development',
  'Evidence gathering and documentation',
  'Legal representation at hearings',
  'Expert witness coordination',
  'Appeal bundle preparation',
  'Post-decision support',
];

const process = [
  {
    title: 'Initial Assessment',
    description: 'Review your case and identify grounds for appeal',
  },
  {
    title: 'Strategy Development',
    description: 'Create a comprehensive appeal strategy',
  },
  {
    title: 'Evidence Collection',
    description: 'Gather supporting documents and expert reports',
  },
  {
    title: 'Appeal Preparation',
    description: 'Draft appeal grounds and prepare bundle',
  },
  {
    title: 'Representation',
    description: 'Expert representation at appeal hearing',
  },
];

const Appeals = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Gavel sx={{ fontSize: 64, mb: 3, color: theme.palette.secondary.light }} />
              <Typography variant="h1" gutterBottom>
                UK Visa Appeals Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Expert support for challenging visa refusals and negative decisions
              </Typography>
              <Button
                component={RouterLink}
                to="/consultation"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mr: 2 }}
              >
                Free Consultation
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overview Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom>
              Appeal Support
            </Typography>
            <Typography variant="body1" paragraph>
              Receiving a visa refusal can be devastating, but you have options to challenge the decision. Our expert team of immigration lawyers specializes in appeals, administrative reviews, and judicial reviews of immigration decisions.
            </Typography>
            <Typography variant="body1">
              We provide comprehensive support throughout the appeal process, from initial assessment to representation at hearings, ensuring the strongest possible case for overturning negative decisions.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Our Services
                </Typography>
                <List>
                  {services.map((service) => (
                    <ListItem key={service}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={service} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Appeal Types Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Types of Appeals
          </Typography>
          <Grid container spacing={4}>
            {appealTypes.map((type) => (
              <Grid item xs={12} md={6} key={type.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                      {type.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {type.description}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Timeframe: {type.timeframe}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Grounds for Appeal:
                    </Typography>
                    <List>
                      {type.grounds.map((ground) => (
                        <ListItem key={ground}>
                          <ListItemIcon>
                            <Assignment color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={ground} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Appeal Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Appeal Process
          </Typography>
          <Grid container spacing={3}>
            {process.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Timeline color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Step {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Expert Representation
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    Our team of experienced immigration lawyers has a strong track record in successfully challenging visa refusals. We understand the complexities of immigration law and appeal procedures.
                  </Typography>
                  <Typography variant="body1">
                    We have helped numerous clients overturn negative decisions through appeals, administrative reviews, and judicial reviews.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Balance color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Comprehensive Support
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    We provide end-to-end support throughout your appeal process, from initial case assessment to final hearing representation.
                  </Typography>
                  <Typography variant="body1">
                    Our services include detailed case analysis, evidence gathering, expert witness coordination, and professional representation to maximize your chances of success.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Need Help with Your Appeal?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Contact us today for a free consultation and expert guidance
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Appeals; 